import { withAlpha } from "@/lib/color";
import { useEffect, useState } from "react";
import { Flame, Sparkles, Trash2, Repeat } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * MarketNews — articoli AI sugli affari di mercato più clamorosi (Original PES).
 * Generati con gpt-5-mini alla conclusione dell'affare, scadono dopo 7 giorni.
 */
function fmtEur(v) {
    const n = Number(v || 0);
    if (n >= 1_000_000) return `€${(n / 1_000_000).toFixed(0)}M`;
    if (n > 0) return `€${n.toLocaleString("it-IT")}`;
    return "—";
}

// Etichette per i prestiti: badge in alto + sottotitolo sotto la cifra.
function loanInfo(kind) {
    switch (kind) {
        case "loan": return { badge: "PRESTITO", sub: "fee prestito" };
        case "loan_option": return { badge: "PRESTITO", sub: "+ riscatto opz." };
        case "loan_obligation": return { badge: "PRESTITO", sub: "obbligo riscatto" };
        default: return null;
    }
}

function timeAgo(iso) {
    if (!iso) return "";
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 3600) return `${Math.max(1, Math.floor(diff / 60))} min fa`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} h fa`;
    return `${Math.floor(diff / 86400)} g fa`;
}

export default function MarketNews({ limit = 10 }) {
    const { user } = useAuth() || {};
    const isAdmin = user?.role === "admin";
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = () => {
        api.get("/market/news", { params: { limit } })
            .then(({ data }) => setItems(data || []))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(); }, [limit]);

    const remove = async (id) => {
        if (!window.confirm("Eliminare questa notizia di mercato?")) return;
        try {
            await api.delete(`/market/news/${id}`);
            setItems((prev) => prev.filter((n) => n.id !== id));
        } catch {
            alert("Errore nell'eliminazione");
        }
    };

    if (loading) {
        return <div className="op-label text-t8f95a3 animate-pulse" data-testid="market-news-loading">// caricamento notizie…</div>;
    }
    if (items.length === 0) {
        return (
            <div className="op-card p-5 text-t8f95a3 text-sm" data-testid="market-news-empty">
                Nessuna notizia di mercato al momento. Gli affari più clamorosi compariranno qui.
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="market-news">
            {items.map((n) => {
                const bomba = n.hype === "bomba";
                const accent = bomba ? "rgb(var(--tc-ff4d4d))" : "rgb(var(--tc-2bb6ff))";
                return (
                    <article
                        key={n.id}
                        className="op-card p-0 overflow-hidden flex flex-col border-l-4"
                        style={{ borderLeftColor: accent }}
                        data-testid={`market-news-item-${n.id}`}
                    >
                        <div className="flex items-stretch gap-3 p-4">
                            {n.player_image && (
                                <img
                                    src={n.player_image}
                                    alt={n.player_name}
                                    className="w-16 h-16 rounded-lg object-cover border shrink-0"
                                    style={{ borderColor: `${withAlpha(accent, "55")}` }}
                                />
                            )}
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                                    <span
                                        className="op-label text-[9px] px-2 py-0.5 border inline-flex items-center gap-1"
                                        style={{ color: accent, borderColor: `${withAlpha(accent, "66")}`, background: `${withAlpha(accent, "0F")}` }}
                                    >
                                        {bomba ? <Flame size={11} /> : <Sparkles size={11} />}
                                        {bomba ? "NOTIZIA BOMBA" : "GRANDE COLPO"}
                                    </span>
                                    {loanInfo(n.kind) && (
                                        <span
                                            className="op-label text-[9px] px-2 py-0.5 border inline-flex items-center gap-1"
                                            style={{ color: "rgb(var(--tc-ff9d2b))", borderColor: "rgb(var(--tc-ff9d2b) / 0.4)", background: "rgb(var(--tc-ff9d2b) / 0.059)" }}
                                            data-testid={`market-news-loan-badge-${n.id}`}
                                        >
                                            <Repeat size={11} /> {loanInfo(n.kind).badge}
                                        </span>
                                    )}
                                    <span className="op-mono text-[10px] text-t5c6370">{timeAgo(n.created_at)}</span>
                                    {isAdmin && (
                                        <button
                                            onClick={() => remove(n.id)}
                                            className="ml-auto text-t5c6370 hover:text-tff4d4d transition-colors"
                                            title="Elimina notizia"
                                            data-testid={`market-news-delete-${n.id}`}
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    )}
                                </div>
                                <h3 className="op-heading text-base leading-tight text-tffffff" data-testid={`market-news-title-${n.id}`}>
                                    {n.title}
                                </h3>
                            </div>
                        </div>
                        <p className="px-4 text-sm text-tc9ccd3 leading-relaxed">{n.body}</p>
                        <div className="mt-auto flex items-center justify-between gap-2 px-4 py-3 mt-3 border-t border-t16181c">
                            <div className="flex items-center gap-2 op-mono text-[11px] text-t8f95a3 min-w-0">
                                {n.seller_logo && <img src={n.seller_logo} alt="" className="w-5 h-5 object-contain shrink-0" />}
                                <span className="truncate">{n.seller_name}</span>
                                <span style={{ color: accent }}>→</span>
                                {n.buyer_logo && <img src={n.buyer_logo} alt="" className="w-5 h-5 object-contain shrink-0" />}
                                <span className="truncate">{n.buyer_name}</span>
                            </div>
                            <span className="op-mono text-sm font-bold shrink-0 text-right" style={{ color: accent }}>
                                {fmtEur(n.total_amount || n.cash_amount)}
                                {loanInfo(n.kind) && (
                                    <span className="block op-label text-[9px] text-tff9d2b font-normal">{loanInfo(n.kind).sub}</span>
                                )}
                            </span>
                        </div>
                    </article>
                );
            })}
        </div>
    );
}
